import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, BookOpen, Search, Filter, Lock, Download, Layers, Flame, Share2, X as XIcon, Linkedin, Facebook, Copy, Check } from "lucide-react";

const CAT_LABEL = {
  contract: "Contracts", script: "Scripts", calculator: "Calculators",
  checklist: "Checklists", vendor: "Vendor Dirs", video: "Videos",
  glossary: "Glossary",
};

const CAT_TONE = {
  contract: "bg-amber-50 text-amber-800",
  script: "bg-violet-50 text-violet-800",
  calculator: "bg-emerald-50 text-emerald-800",
  checklist: "bg-sky-50 text-sky-800",
  vendor: "bg-indigo-50 text-indigo-800",
  video: "bg-rose-50 text-rose-800",
  glossary: "bg-slate-100 text-slate-800",
};

/**
 * Public, unauthenticated /resources library — SEO + lead-gen surface.
 * Pulls free-tier metadata only; full body requires signup.
 */
export default function PublicResources() {
  const { user } = useAuth?.() || { user: null };
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);

  // Parse referral URL params once on mount so RecipeCard can attribute the
  // capture back to the user who shared the link.
  const params = new URLSearchParams(location.search);
  const incomingRef = params.get("ref") || "";
  const incomingChannel = params.get("channel") || "";
  const focusedRecipe = params.get("recipe") || "";

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/resources/all`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setItems(d || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/stack-recipes`)
      .then((r) => (r.ok ? r.json() : { recipes: [] }))
      .then((d) => setRecipes(d.recipes || []))
      .catch(() => setRecipes([]));
  }, []);

  // Auto-scroll to the recipe specified in ?recipe=slug (shared deep-link).
  useEffect(() => {
    if (!focusedRecipe || recipes.length === 0) return;
    const el = document.querySelector(`[data-testid="stack-recipe-card-${focusedRecipe}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusedRecipe, recipes.length]);

  const cats = Array.from(new Set(items.map((i) => i.category)));
  const filtered = items.filter((i) => {
    if (cat && i.category !== cat) return false;
    if (!q) return true;
    const hay = `${i.title} ${i.summary} ${(i.tags || []).join(" ")}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <header className="mkt-section">
        <div className="text-center max-w-3xl mx-auto">
          <div className="mkt-eyebrow inline-flex items-center gap-1.5">
            <BookOpen size={12} /> Free wholesaler library
          </div>
          <h1 className="mkt-h1 mt-3">
            Templates, scripts, checklists & calculators — yours free.
          </h1>
          <p className="text-[var(--ink-2)] text-lg mt-4 leading-relaxed">
            The exact resources working wholesalers use to land deals.
            Free to browse · sign up to unlock full content + the live calculators.
          </p>
        </div>
      </header>

      {recipes.length > 0 && (
        <StackRecipesSection
          recipes={recipes}
          currentUser={user}
          incomingRef={incomingRef}
          incomingChannel={incomingChannel}
        />
      )}

      {/* Toolbar */}
      <div className="mkt-section py-0">
        <div className="max-w-5xl mx-auto card p-3 flex flex-col gap-3" data-testid="public-resources-toolbar">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, summary, or tag…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]"
              data-testid="public-resources-search"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Chip active={!cat} onClick={() => setCat("")} label={`All (${items.length})`} />
            {cats.map((c) => (
              <Chip
                key={c}
                active={cat === c}
                onClick={() => setCat(c === cat ? "" : c)}
                label={`${CAT_LABEL[c] || c} (${items.filter((i) => i.category === c).length})`}
                tone={CAT_TONE[c]}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="mkt-section">
        {loading ? (
          <div className="text-center text-[var(--ink-3)] py-12">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-[var(--ink-3)] py-12">
            <Filter size={24} className="inline mb-2 opacity-60" />
            <div>No resources match your filter.</div>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="public-resources-grid">
            {filtered.map((r) => (
              <Link
                key={r.id}
                to={`/resources/${r.slug}`}
                className="card p-5 hover:shadow-lg transition-shadow flex flex-col gap-2 group"
                data-testid="public-resource-card"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={"px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide " + (CAT_TONE[r.category] || "bg-slate-100 text-slate-700")}>
                    {CAT_LABEL[r.category] || r.category}
                  </span>
                  <span className="text-[10px] font-bold text-emerald-700 uppercase">Free</span>
                </div>
                <h3 className="font-display font-bold text-lg leading-snug group-hover:text-[var(--primary)] transition-colors">
                  {r.title}
                </h3>
                <p className="text-sm text-[var(--ink-2)] line-clamp-3 leading-relaxed flex-1">
                  {r.summary}
                </p>
                <div className="text-sm font-semibold text-[var(--ink)] flex items-center gap-1 mt-2 pt-2 border-t border-slate-100">
                  Read full <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="mkt-section text-center bg-slate-50 mt-12 py-16">
        <div className="max-w-2xl mx-auto">
          <Lock size={32} className="inline mb-3 text-[var(--primary)]" />
          <h2 className="mkt-h2">Want the full library + the live calculators?</h2>
          <p className="text-[var(--ink-2)] text-base mt-3 mb-6">
            Sign up free to unlock every resource in full, plus the AI Deal Analyzer,
            buyer list builder, and CRM. No credit card.
          </p>
          <Link to="/register" className="btn btn-primary inline-flex items-center gap-2" data-testid="public-resources-signup-cta">
            Get the full library — free <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function PublicNav() {
  return (
    <header className="border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
        <Link to="/" className="font-display font-extrabold text-xl tracking-tight">
          iSpeedToClose
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)]">Home</Link>
          <Link to="/login" className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)]">Log in</Link>
          <Link to="/register" className="btn btn-primary">Sign up free</Link>
        </div>
      </div>
    </header>
  );
}

function Chip({ active, onClick, label, tone }) {
  const base = active
    ? "bg-[var(--primary)] text-white border-[var(--primary)]"
    : "bg-white border-slate-200 text-[var(--ink-2)] hover:border-slate-400";
  return (
    <button type="button" onClick={onClick}
      className={"inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " + base}>
      {label}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Stack Recipe Library — featured at the top of /resources.
// Public lead-magnet surface; download CSV template is unauthenticated.
// ──────────────────────────────────────────────────────────────────────────
function StackRecipesSection({ recipes, currentUser, incomingRef, incomingChannel }) {
  return (
    <section className="mkt-section pb-0" data-testid="stack-recipes-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
          <div className="min-w-0">
            <div className="mkt-eyebrow inline-flex items-center gap-1.5">
              <Layers size={12} /> Stack recipe library · new
            </div>
            <h2 className="mkt-h2 mt-3">The motivated-seller stacks that actually convert.</h2>
            <p className="text-[var(--ink-2)] mt-2 max-w-2xl leading-relaxed">
              Pre-built list combos used by 7-figure wholesalers. Each recipe shows you which lists to pull,
              why they stack, and includes a ready-to-import CSV template — column-mapped for iSpeedToClose's
              auto-distress enrichment.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stack-recipes-grid">
          {recipes.map((r) => (
            <RecipeCard
              key={r.slug}
              recipe={r}
              currentUser={currentUser}
              incomingRef={incomingRef}
              incomingChannel={incomingChannel}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function RecipeCard({ recipe, currentUser, incomingRef, incomingChannel }) {
  const hot = recipe.motivation_score >= 90;
  const tierTone = recipe.tier_required === "hustler"
    ? "bg-violet-100 text-violet-700"
    : "bg-emerald-100 text-emerald-700";
  const [captureOpen, setCaptureOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Build a referral URL anchored to THIS user (or anon if logged out).
  // Channel param is appended at the moment a specific share button is clicked.
  const buildShareUrl = (channel) => {
    const base = `${window.location.origin}/resources?recipe=${recipe.slug}`;
    const ref = currentUser?.id ? `&ref=${currentUser.id}` : "";
    return `${base}${ref}&channel=${channel}`;
  };

  const shareText = `🔥 ${recipe.name} — a motivated-seller stack that converts at ${recipe.motivation_score}/100. Grab the free CSV template:`;

  const openSharePopup = (channel) => {
    const url = buildShareUrl(channel);
    let target;
    if (channel === "twitter") {
      target = `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`;
    } else if (channel === "linkedin") {
      target = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    } else if (channel === "facebook") {
      target = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    } else {
      return;
    }
    window.open(target, "_blank", "noopener,noreferrer,width=600,height=500");
  };

  const handleCopy = async () => {
    const url = buildShareUrl("copy");
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("Email required"); return; }
    setBusy(true);
    setError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/stack-recipes/${recipe.slug}/capture`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email.trim(),
            name: name.trim(),
            source: "resources",
            referrer_user_id: incomingRef || "",
            share_channel: incomingChannel || "",
          }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || "Something went wrong. Please try again.");
        setBusy(false);
        return;
      }
      const body = await res.json();
      const link = document.createElement("a");
      link.href = `${process.env.REACT_APP_BACKEND_URL}${body.download_url}`;
      link.setAttribute("download", `${recipe.slug}-template.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setCaptureOpen(false);
      setEmail("");
      setName("");
    } catch (_err) {
      setError("Network error. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      className="card p-5 flex flex-col gap-3 group hover:shadow-lg transition-shadow"
      data-testid={`stack-recipe-card-${recipe.slug}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-2xl leading-none" aria-hidden>{recipe.icon}</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${tierTone}`}>
            {recipe.tier_required === "hustler" ? "Hustler+" : "Pro+"}
          </span>
          {hot && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-rose-600 text-white text-[10px] font-bold uppercase tracking-wider">
              <Flame size={10} /> Hot
            </span>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] font-bold uppercase text-[var(--ink-3)]">Motivation</div>
          <div className="font-display font-extrabold text-xl tabular-nums leading-none" style={{ color: hot ? "#e11d48" : "#6366f1" }}>
            {recipe.motivation_score}
          </div>
        </div>
      </div>
      <h3 className="font-display font-extrabold text-lg leading-snug">{recipe.name}</h3>
      <p className="text-sm text-[var(--ink-2)] leading-relaxed">{recipe.blurb}</p>
      <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--ink-3)] mt-1">Stack these lists</div>
      <ul className="text-xs text-[var(--ink)] space-y-1">
        {recipe.stack_lists.map((s, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <span className="text-[var(--ink-3)] mt-0.5">▸</span>
            <span>{s}</span>
          </li>
        ))}
      </ul>
      <div className="text-[11px] text-[var(--ink-3)] mt-1">
        <strong className="text-[var(--ink-2)]">Typical overlap:</strong> {recipe.expected_overlap_pct}
      </div>
      {!captureOpen && !shareOpen ? (
        <div className="flex items-center gap-2 mt-2 pt-3 border-t border-slate-100 flex-wrap">
          <button
            type="button"
            onClick={() => { setCaptureOpen(true); setShareOpen(false); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--primary)] text-white text-xs font-bold hover:opacity-90 transition-opacity"
            data-testid={`stack-recipe-download-${recipe.slug}`}
          >
            <Download size={12} /> CSV template
          </button>
          <Link
            to="/register"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-[var(--ink-2)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
            data-testid={`stack-recipe-cta-${recipe.slug}`}
          >
            Try it free <ArrowRight size={12} />
          </Link>
          <button
            type="button"
            onClick={() => { setShareOpen(true); setCaptureOpen(false); }}
            className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-[var(--ink-3)] hover:bg-slate-100 hover:text-[var(--ink)] transition-colors"
            data-testid={`stack-recipe-share-${recipe.slug}`}
            aria-label="Share this recipe"
            title="Share this recipe"
          >
            <Share2 size={13} /> Share
          </button>
        </div>
      ) : shareOpen ? (
        <div className="mt-2 pt-3 border-t border-slate-100 flex flex-col gap-2" data-testid={`stack-recipe-share-panel-${recipe.slug}`}>
          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--ink-3)]">
            Share — every capture from your link will attribute to you
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => openSharePopup("twitter")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-[var(--ink-2)] hover:border-black hover:text-black transition-colors"
              data-testid={`stack-recipe-share-twitter-${recipe.slug}`}
              aria-label="Share on X / Twitter"
            >
              <XIcon size={13} /> X
            </button>
            <button
              type="button"
              onClick={() => openSharePopup("linkedin")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-[var(--ink-2)] hover:border-[#0A66C2] hover:text-[#0A66C2] transition-colors"
              data-testid={`stack-recipe-share-linkedin-${recipe.slug}`}
              aria-label="Share on LinkedIn"
            >
              <Linkedin size={13} /> LinkedIn
            </button>
            <button
              type="button"
              onClick={() => openSharePopup("facebook")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-[var(--ink-2)] hover:border-[#1877F2] hover:text-[#1877F2] transition-colors"
              data-testid={`stack-recipe-share-facebook-${recipe.slug}`}
              aria-label="Share on Facebook"
            >
              <Facebook size={13} /> Facebook
            </button>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-[var(--ink-2)] hover:border-emerald-600 hover:text-emerald-700 transition-colors"
              data-testid={`stack-recipe-share-copy-${recipe.slug}`}
              aria-label="Copy share link"
            >
              {copied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy link</>}
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(false)}
              className="ml-auto px-2.5 py-1.5 rounded-lg text-[var(--ink-3)] text-xs hover:text-[var(--ink)]"
              data-testid={`stack-recipe-share-close-${recipe.slug}`}
              aria-label="Close share panel"
            >
              Done
            </button>
          </div>
          {!currentUser && (
            <p className="text-[10px] text-[var(--ink-3)] leading-snug">
              💡 <Link to="/login" className="underline font-semibold">Log in</Link> first so captures from your share link attribute to your account.
            </p>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-2 pt-3 border-t border-slate-100 flex flex-col gap-2" data-testid={`stack-recipe-capture-${recipe.slug}`}>
          <input
            type="email"
            required
            placeholder="Your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]"
            data-testid={`stack-recipe-capture-email-${recipe.slug}`}
            autoFocus
          />
          <input
            type="text"
            placeholder="Your first name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]"
            data-testid={`stack-recipe-capture-name-${recipe.slug}`}
          />
          {error && <div className="text-[11px] text-rose-600 font-semibold">{error}</div>}
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[var(--primary)] text-white text-xs font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
              data-testid={`stack-recipe-capture-submit-${recipe.slug}`}
            >
              {busy ? "Sending…" : (<><Download size={12} /> Email me the template</>)}
            </button>
            <button
              type="button"
              onClick={() => { setCaptureOpen(false); setError(""); }}
              className="px-2.5 py-2 rounded-lg text-[var(--ink-3)] text-xs hover:text-[var(--ink)]"
              data-testid={`stack-recipe-capture-cancel-${recipe.slug}`}
            >
              Cancel
            </button>
          </div>
          <p className="text-[10px] text-[var(--ink-3)] leading-snug">
            We'll email you the CSV + a 5-day tactical drip on how to use it. Unsubscribe anytime.
          </p>
        </form>
      )}
    </article>
  );
}

